/**
 * The violation feed across every repo, grouped by rule, plus the helpers its actions need:
 * task drafts and the marker that ties a backlog item to a violation, and waiver status.
 * Pure functions.
 */
import { disciplineRuleInfo } from '@orgus/protocol';
import type { DisciplineRuleInfo, DisciplineWaiver, Id, Violation } from '@orgus/protocol';
import type { RepoRow } from './model.ts';

export interface FeedEntry {
  projectId: Id;
  projectName: string;
  violation: Violation;
  /** Stable per violation: project, rule, path and line. */
  key: string;
}

export interface FeedGroup {
  rule: string;
  info: DisciplineRuleInfo;
  errors: number;
  warns: number;
  /** Repos with at least one violation of this rule. */
  repos: number;
  entries: FeedEntry[];
  /** Violations counted but not carried by the reports (lists are capped per rule). */
  hidden: number;
}

export interface FeedFilter {
  projectId?: Id | null;
  severity?: 'all' | 'error' | 'warn';
  /** Show baseline debt too (default: only new violations when a repo has a ratchet). */
  debt?: boolean;
}

export const entryKey = (projectId: Id, v: Violation) => `${projectId}|${v.rule}|${v.path}|${v.line ?? 0}`;

/** Every violation the newest reports carry, grouped by rule, worst rule first. */
export function violationFeed(rows: readonly RepoRow[], f: FeedFilter = {}): FeedGroup[] {
  const groups = new Map<string, FeedGroup & { repoIds: Set<Id> }>();
  for (const row of rows) {
    const r = row.report;
    if (!r || r.error || (f.projectId && row.projectId !== f.projectId)) continue;
    for (const v of r.violations) {
      if (f.severity && f.severity !== 'all' && v.severity !== f.severity) continue;
      if (v.debt && !f.debt) continue;
      let g = groups.get(v.rule);
      if (!g) groups.set(v.rule, (g = { rule: v.rule, info: disciplineRuleInfo(v.rule), errors: 0, warns: 0, repos: 0, entries: [], hidden: 0, repoIds: new Set() }));
      g.entries.push({ projectId: row.projectId, projectName: row.name, violation: v, key: entryKey(row.projectId, v) });
      if (v.severity === 'error') g.errors++;
      else g.warns++;
      g.repoIds.add(row.projectId);
    }
    // Counts the report knows but did not carry.
    for (const s of r.rules ?? []) {
      const g = groups.get(s.rule);
      if (!g || !g.repoIds.has(row.projectId)) continue;
      const carried = r.violations.filter((v) => v.rule === s.rule).length;
      g.hidden += Math.max(0, s.errors + s.warns - carried);
    }
  }
  return [...groups.values()]
    .map(({ repoIds, ...g }) => ({ ...g, repos: repoIds.size }))
    .sort((a, b) => b.errors - a.errors || b.warns - a.warns || a.rule.localeCompare(b.rule));
}

/** Written into a backlog item's body, so the feed can show which violations have a task. */
export const taskMarker = (projectId: Id, v: Pick<Violation, 'rule' | 'path'>) => `orgus-discipline:${projectId}:${v.rule}:${v.path}`;

export interface TaskLike {
  id: Id;
  title: string;
  body: string;
  status: string;
}

/** Open backlog items by the violation marker they carry. */
export function openTasks(items: readonly TaskLike[]): Map<string, TaskLike> {
  const out = new Map<string, TaskLike>();
  for (const it of items) {
    if (it.status === 'done' || it.status === 'dropped') continue;
    for (const m of it.body.matchAll(/orgus-discipline:[^\s`]+/g)) out.set(m[0], it);
  }
  return out;
}

export interface TaskDraft {
  projectId: Id;
  kind: 'chore';
  title: string;
  body: string;
  priority: 1 | 2;
}

/** A backlog item for one rule's violations in one repo. */
export function taskDraft(projectId: Id, projectName: string, vs: readonly Violation[]): TaskDraft {
  const first = vs[0]!;
  const info = disciplineRuleInfo(first.rule);
  const where = vs.length === 1 ? first.path : `${vs.length} places`;
  const lines = [
    `${info.why}`,
    '',
    `How to fix: ${info.fix}`,
    '',
    ...vs.slice(0, 30).map((v) => `- ${v.path}${v.line ? `:${v.line}` : ''}: ${v.message}`),
    ...(vs.length > 30 ? [`- and ${vs.length - 30} more`] : []),
    '',
    `Opened from the Discipline page (rule ${first.rule}).`,
    ...vs.map((v) => `\`${taskMarker(projectId, v)}\``),
  ];
  return { projectId, kind: 'chore', title: `${info.title}: fix ${where} in ${projectName}`.slice(0, 200), body: lines.join('\n'), priority: vs.some((v) => v.severity === 'error') ? 1 : 2 };
}

export type WaiverStatus = 'active' | 'expiring' | 'expired' | 'revoked';

const DAY = 86_400_000;

export function waiverDaysLeft(w: Pick<DisciplineWaiver, 'expires'>, now: number): number {
  const end = Date.parse(`${w.expires}T00:00:00Z`);
  return Math.floor((end - Math.floor(now / DAY) * DAY) / DAY);
}

export function waiverStatus(w: DisciplineWaiver, now: number, soonDays = 7): WaiverStatus {
  if (w.revokedAt) return 'revoked';
  const left = waiverDaysLeft(w, now);
  if (left < 0) return 'expired';
  return left <= soonDays ? 'expiring' : 'active';
}

/** YYYY-MM-DD, `days` from now (UTC, as waivers are compared). */
export function dateIn(days: number, now: number): string {
  return new Date(Math.floor(now / DAY) * DAY + days * DAY).toISOString().slice(0, 10);
}

/** The repo file the house rules (and the design gate) read waivers from. */
export const REPO_WAIVERS_FILE = '.orgus/waivers.yaml';
/** The folder layout gate (orgus layout) keeps its own exceptions here; they never touch a score. */
export const LAYOUT_WAIVERS_FILE = '.orgus/waivers.json';

/** A waiver's rule may be a glob ('design/*', '*'): does it cover this rule id? */
export function waiverRuleMatches(pattern: string, rule: string): boolean {
  if (pattern === '*' || pattern === rule) return true;
  return pattern.endsWith('/*') && rule.startsWith(pattern.slice(0, -1));
}

export interface RepoFileWaivers {
  projectId: Id;
  projectName: string;
  /** Violations waived in the last check by the repo's own file. */
  waived: number;
  /** The rules they belong to, most waived first. */
  rules: { rule: string; title: string; waived: number }[];
}

/**
 * Violations each repo's own .orgus/waivers.yaml waived in its last check: rules with waived
 * violations that no open waiver kept in Orgus covers. Repos with none are left out.
 */
export function repoFileWaivers(rows: readonly RepoRow[], waivers: readonly DisciplineWaiver[], now: number): RepoFileWaivers[] {
  const out: RepoFileWaivers[] = [];
  for (const row of rows) {
    const r = row.report;
    if (!r || r.error) continue;
    const mine = waivers.filter((w) => w.projectId === row.projectId && (waiverStatus(w, now) === 'active' || waiverStatus(w, now) === 'expiring'));
    const rules = (r.rules ?? [])
      .filter((s) => s.waived > 0 && !mine.some((w) => waiverRuleMatches(w.rule, s.rule)))
      .map((s) => ({ rule: s.rule, title: disciplineRuleInfo(s.rule).title, waived: s.waived }))
      .sort((a, b) => b.waived - a.waived);
    if (rules.length) out.push({ projectId: row.projectId, projectName: row.name, waived: rules.reduce((n, x) => n + x.waived, 0), rules });
  }
  return out;
}
